import { useState } from "react";
import { Navigate, useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { BusinessSearch } from "@/components/BusinessSearch";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Scale, Building2, CheckCircle2, ArrowRight, MapPin, UserRound, Hash } from "lucide-react";
import { toast } from "sonner";
import { useSosEnabled } from "@/lib/siteConfig";

const DEST = { company: "/sos-forms", attorney_firm: "/matters" };

export default function OnboardingBusiness() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const [attached, setAttached] = useState(null);
  const sosOn = useSosEnabled();

  if (loading || user === null) return <div className="min-h-screen grid place-items-center text-muted-foreground">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;

  const isFirm = user.account_type === "attorney_firm";
  const dest = (user.account_type === "company" && !sosOn ? "/forms" : DEST[user.account_type]) || "/dashboard";

  const attach = async (biz) => {
    setSaving(true);
    try {
      await api.post("/business/attach", biz);
      setAttached(biz);
      toast.success(`${biz.entity_name} verified & attached to your profile.`);
    } catch {
      toast.error("Could not save that business. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-secondary/30">
      <div className="max-w-3xl mx-auto px-6 py-12">
        <Link to="/" className="flex items-center gap-2 w-fit mb-10">
          <span className="grid place-items-center w-9 h-9 bg-primary text-primary-foreground"><Scale size={18} /></span>
          <span className="font-serif text-xl font-semibold">NC Legal Forms</span>
        </Link>

        {attached ? (
          <div data-testid="business-confirmation">
            <p className="overline text-accent mb-3 flex items-center gap-2"><CheckCircle2 size={14} /> Verified · Secretary of State records</p>
            <h1 className="font-serif text-4xl tracking-tight mb-3">You’re all set</h1>
            <p className="text-muted-foreground mb-8 max-w-xl">
              We attached this {isFirm ? "firm" : "business"} to your profile — its details will autofill on your forms.
            </p>

            <div className="bg-background border-[3px] border-black p-6" data-testid="business-confirmation-card">
              <div className="flex items-start gap-4">
                <span className="grid place-items-center w-12 h-12 bg-primary text-primary-foreground shrink-0"><Building2 size={24} /></span>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <h2 className="font-serif text-2xl tracking-tight" data-testid="confirm-entity-name">{attached.entity_name}</h2>
                    {attached.status && <Badge className="rounded-sm bg-emerald-600 hover:bg-emerald-600" data-testid="confirm-status">{attached.status}</Badge>}
                  </div>
                  <div className="mt-3 grid sm:grid-cols-2 gap-x-6 gap-y-2 text-sm text-muted-foreground">
                    {attached.sos_id && <p className="flex items-center gap-1.5" data-testid="confirm-sos-id"><Hash size={13} className="text-accent" /> SOS ID <span className="text-foreground font-medium">{attached.sos_id}</span></p>}
                    {attached.entity_type && <p className="flex items-center gap-1.5"><Building2 size={13} className="text-accent" /> {attached.entity_type}</p>}
                    {attached.registered_agent && <p className="flex items-center gap-1.5"><UserRound size={13} className="text-accent" /> Agent: <span className="text-foreground">{attached.registered_agent}</span></p>}
                    {attached.state && <p className="flex items-center gap-1.5"><MapPin size={13} className="text-accent" /> {attached.state}</p>}
                  </div>
                  {attached.address && <p className="mt-2 text-xs text-muted-foreground flex items-start gap-1.5"><MapPin size={12} className="mt-0.5" /> {attached.address}</p>}
                </div>
              </div>
            </div>

            <div className="mt-6 flex flex-wrap items-center gap-3">
              <Button onClick={() => navigate(dest)} data-testid="business-continue-btn"
                className="rounded-sm gap-2 bg-primary hover:bg-accent hover:text-accent-foreground transition-colors">
                Continue to {isFirm ? "my cases" : "SOS forms"} <ArrowRight size={16} />
              </Button>
              <Button variant="ghost" onClick={() => setAttached(null)} data-testid="business-change-btn"
                className="rounded-sm text-muted-foreground hover:text-foreground">Choose a different business</Button>
            </div>
          </div>
        ) : (
          <>
            <p className="overline text-accent mb-3 flex items-center gap-2"><Building2 size={14} /> Step 2 of 2 · {isFirm ? "Your firm" : "Your business"}</p>
            <h1 className="font-serif text-4xl tracking-tight mb-3">Verify {isFirm ? "your firm" : "your business"}</h1>
            <p className="text-muted-foreground mb-8 max-w-xl">
              Look up your {isFirm ? "firm" : "business"} in the official Secretary of State records. We’ll attach it to your profile so your entity name and registered agent autofill on every form.
            </p>

            <div className="bg-background border-2 border-black/10 p-6" data-testid="onboarding-business-card">
              <BusinessSearch onSelect={attach} defaultQuery={user.company || ""} defaultState="NC" />
            </div>

            <div className="mt-6 flex items-center gap-4">
              <Button variant="ghost" disabled={saving} onClick={() => navigate(dest)} data-testid="onboarding-skip-btn"
                className="rounded-sm gap-2 text-muted-foreground hover:text-foreground">
                Skip for now <ArrowRight size={16} />
              </Button>
              <span className="text-xs text-muted-foreground flex items-center gap-1"><CheckCircle2 size={13} className="text-accent" /> You can do this later from your profile</span>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

